/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 7b: a program's settings, in plain sentences. Each save sends only
 * what changed and reports back through onChanged once the server agrees.
 */
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Input,
  Label,
  ReadOnlyIcon,
  Switch,
  Textarea,
  cn,
} from "@lemonadetraining/web-ui";
import {
  NOTIFICATION_TYPE,
  archiveProduct,
  notifyActions,
  updateProduct,
  updateProductCoverURL,
  updateProgrammingControlSetting,
  uploadCoverArt,
} from "@lemonadetraining/web-shared";
import * as PRODUCTS from "@lemonadetraining/web-shared/constants/ProductConst";
import { messageForUploadError } from "@lemonadetraining/web-shared/utils/imageResize";
import VariantImg from "@lemonadetraining/web-shared/components/VariantImg";
import DiscontinueProductDialog from "../Box/DiscontinueProductDialog";
import { Fact, SettingsCard } from "../gymSettings/SettingsCard";
import { useCanDiscontinue, useDiscontinueProduct } from "../../hooks/useDiscontinueProduct";
import { callableCode, callableMessage, isPermissionDenied } from "../../lib/callableError";
import { DESCRIPTION_MAX, NAME_MAX, NAME_MIN } from "../../lib/newProgram";
import { programPriceLabel, programTypeLabel } from "../../lib/programLabels";
import { ArchiveProgramDialog } from "./ArchiveProgramDialog";
import { runsAs, type ProgramDoc } from "./programTypes";

export interface ProgramSettingsTabProps {
  program: ProgramDoc;
  canManage: boolean;
  onChanged: (patch: Partial<ProgramDoc>) => void;
  /** Named in the view-only line; falls back to "The coach who created it". */
  creatorName?: string;
}

type Toggle = "landingPage" | "isHistoryAvailable" | "releaseAll";

const WHO_CAN_CHANGE =
  "You don't have permission to change this. If you think you should, ask your gym's owner.";

/** An error SettingsCard shows as it is. */
const saveError = (error: unknown) => {
  const code = callableCode(error);
  const userMessage = isPermissionDenied(error)
    ? WHO_CAN_CHANGE
    : code === "invalid-argument" && callableMessage(error)
      ? callableMessage(error)
      : "Couldn't save. Nothing changed. Try again.";
  return Object.assign(new Error("save failed"), { userMessage });
};

export function nameError(raw: string): string | null {
  const name = raw.trim();
  if (!name) return "Enter a name.";
  if (name.length < NAME_MIN) return `Use at least ${NAME_MIN} characters for the name.`;
  if (name.length > NAME_MAX) return `Keep the name to ${NAME_MAX} characters or fewer.`;
  return null;
}

function SwitchRow({
  id,
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex min-h-11 items-start gap-3">
      <div className="flex flex-1 flex-col gap-0.5">
        <Label htmlFor={id}>{label}</Label>
        <span id={`${id}-hint`} className="text-sm text-muted-foreground">
          {hint}
        </span>
      </div>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        aria-describedby={`${id}-hint`}
        data-testid={id}
      />
    </div>
  );
}

export function ProgramSettingsTab({
  program,
  canManage,
  onChanged,
  creatorName,
}: ProgramSettingsTabProps) {
  const dispatch = useDispatch();
  // Storage only lets a user write under their own uid, so the cover goes under the editor.
  const editorUID: string | null = useSelector(
    (state: { user: { user: { uid?: string } | null } }) => state.user.user?.uid ?? null
  );
  const programmingUID = program.programmingUID;
  const archived = !program.active;
  const isPT = Boolean(program.isPT);
  const daily = runsAs(program) === "daily";

  const [draft, setDraft] = useState({ name: "", description: "" });
  const [aboutErrors, setAboutErrors] = useState<{ name?: string; description?: string }>({});
  const [toggles, setToggles] = useState<Partial<Record<Toggle, boolean>>>({});
  const [toggleSaving, setToggleSaving] = useState<Toggle | null>(null);
  const [toggleError, setToggleError] = useState<{ key: Toggle; message: string } | null>(null);
  const [coverSaving, setCoverSaving] = useState(false);
  const [coverError, setCoverError] = useState<string | null>(null);
  const [savedCover, setSavedCover] = useState<string | null>(null);
  const [pickedPreview, setPickedPreview] = useState<string | null>(null);
  const coverInput = useRef<HTMLInputElement>(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const canDiscontinue = useCanDiscontinue();
  const discontinue = useDiscontinueProduct({
    productType: PRODUCTS.TYPE.Programming,
    boxID: program.boxID ?? null,
    noun: "subscription",
    onCompleted: () => onChanged({ active: false }),
  });

  useEffect(
    () => () => {
      if (pickedPreview) URL.revokeObjectURL(pickedPreview);
    },
    [pickedPreview]
  );

  // The switch leans ahead only while its save runs; otherwise the program is the truth
  // (the header's More menu can change the public page too).
  const toggleValue = (key: Toggle) =>
    toggleSaving === key && toggles[key] !== undefined
      ? Boolean(toggles[key])
      : Boolean(program[key]);

  /* ------------ about ------------ */
  const startAbout = () => {
    setDraft({ name: program.name ?? "", description: program.description ?? "" });
    setAboutErrors({});
  };

  const saveAbout = async () => {
    const name = draft.name.trim();
    const description = draft.description.trim();
    const patch: Partial<Pick<ProgramDoc, "name" | "description">> = {};
    if (name !== (program.name ?? "")) patch.name = name;
    if (description !== (program.description ?? "")) patch.description = description;
    const errors = {
      name: "name" in patch ? (nameError(name) ?? undefined) : undefined,
      description:
        "description" in patch && description.length > DESCRIPTION_MAX
          ? `Keep this to ${DESCRIPTION_MAX} characters or fewer.`
          : undefined,
    };
    setAboutErrors(errors);
    if (errors.name || errors.description) return false;
    if (Object.keys(patch).length === 0) return true;
    try {
      await updateProduct(programmingUID, PRODUCTS.TYPE.Programming, patch);
    } catch (error: unknown) {
      throw saveError(error);
    }
    onChanged(patch);
    return true;
  };

  /* ------------ cover ------------ */
  const changeCover = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || coverSaving) return;
    setCoverSaving(true);
    setCoverError(null);
    setPickedPreview(URL.createObjectURL(file));
    try {
      const coverArtURL = await uploadCoverArt(file, editorUID, programmingUID);
      if (!coverArtURL) throw new Error("upload returned no address");
      await updateProductCoverURL(programmingUID, PRODUCTS.TYPE.Programming, coverArtURL);
      setSavedCover(coverArtURL);
      onChanged({ coverArtURL });
    } catch (error: unknown) {
      setCoverError(
        messageForUploadError(error, "Couldn't change the cover picture. Your old one is kept.", {
          notAllowed: WHO_CAN_CHANGE,
        })
      );
    } finally {
      setPickedPreview(null);
      setCoverSaving(false);
    }
  };

  const cover = savedCover ?? program.coverArtURL ?? null;

  /* ------------ switches ------------ */
  const setToggle = async (key: Toggle, next: boolean) => {
    setToggleSaving(key);
    setToggleError(null);
    try {
      await updateProgrammingControlSetting({ programmingUID, settings: { [key]: next } });
      setToggles((t) => ({ ...t, [key]: next }));
      onChanged({ [key]: next });
    } catch (error: unknown) {
      setToggleError({ key, message: saveError(error).userMessage });
    } finally {
      setToggleSaving(null);
    }
  };

  const toggleErrorFor = (keys: Toggle[]) =>
    toggleError && keys.includes(toggleError.key) ? (
      <p role="alert" className="text-sm text-destructive" data-testid="settings-toggle-error">
        {toggleError.message}
      </p>
    ) : null;

  /* ------------ archive ------------ */
  const confirmArchive = async () => {
    setArchiving(true);
    setArchiveError(null);
    try {
      await archiveProduct(programmingUID, PRODUCTS.TYPE.Programming);
      setArchiveOpen(false);
      onChanged({ active: false });
      dispatch(
        notifyActions.sendNotification({
          severity: NOTIFICATION_TYPE.success,
          title: `“${program.name}” is archived`,
          message: "",
        })
      );
    } catch (error: unknown) {
      setArchiveError(
        isPermissionDenied(error)
          ? "Only the coach who created this program and the gym's admins can archive it."
          : "Couldn't archive the program. Nothing changed. Try again."
      );
    } finally {
      setArchiving(false);
    }
  };

  const onOff = (on: boolean) => (on ? "On" : "Off");
  const typeLabel = programTypeLabel(program) ?? "Not set";
  const priceLabel = programPriceLabel(program) ?? "Not set";
  const mayEnd = canManage && canDiscontinue(program);

  return (
    <div className="flex flex-col gap-4" data-testid="program-settings">
      {!canManage && (
        <Alert role="status" data-testid="program-settings-view-only">
          <ReadOnlyIcon aria-hidden="true" className="text-muted-foreground" />
          <AlertTitle>View only</AlertTitle>
          <AlertDescription className="col-start-2">
            {creatorName
              ? `${creatorName} and the gym's admins can change this program.`
              : "The coach who created it and the gym's admins can change this program."}
          </AlertDescription>
        </Alert>
      )}

      <SettingsCard
        id="about"
        title="About"
        description="The name and words members see when they find and follow the program."
        renderEdit={
          canManage
            ? () => (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="program-name">Name</Label>
                    <Input
                      id="program-name"
                      value={draft.name}
                      aria-invalid={Boolean(aboutErrors.name)}
                      aria-describedby="program-name-hint"
                      onChange={(e) => {
                        setDraft((d) => ({ ...d, name: e.target.value }));
                        setAboutErrors((x) => ({ ...x, name: undefined }));
                      }}
                    />
                    <span
                      id="program-name-hint"
                      className={cn(
                        "text-xs",
                        aboutErrors.name ? "text-destructive" : "text-muted-foreground"
                      )}
                      role={aboutErrors.name ? "alert" : undefined}
                    >
                      {aboutErrors.name ?? `${NAME_MIN} to ${NAME_MAX} characters.`}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-baseline justify-between gap-2">
                      <Label htmlFor="program-description">What it's about</Label>
                      <span className="text-xs text-muted-foreground">
                        {draft.description.trim().length} / {DESCRIPTION_MAX}
                      </span>
                    </div>
                    <Textarea
                      id="program-description"
                      rows={6}
                      value={draft.description}
                      aria-invalid={Boolean(aboutErrors.description)}
                      aria-describedby="program-description-hint"
                      onChange={(e) => {
                        setDraft((d) => ({ ...d, description: e.target.value }));
                        setAboutErrors((x) => ({ ...x, description: undefined }));
                      }}
                    />
                    <span
                      id="program-description-hint"
                      className={cn(
                        "text-xs",
                        aboutErrors.description ? "text-destructive" : "text-muted-foreground"
                      )}
                      role={aboutErrors.description ? "alert" : undefined}
                    >
                      {aboutErrors.description ??
                        "Who it's for and what to expect. Members read this before they join."}
                    </span>
                  </div>
                </div>
              )
            : undefined
        }
        onStartEdit={startAbout}
        onSave={saveAbout}
      >
        <dl className="flex flex-col gap-4">
          <Fact label="Name">{program.name}</Fact>
          <Fact label="What it's about">
            {program.description ? (
              <span className="whitespace-pre-line font-normal">{program.description}</span>
            ) : (
              <span className="font-normal text-muted-foreground">No description yet</span>
            )}
          </Fact>
        </dl>
      </SettingsCard>

      <SettingsCard
        id="cover"
        title="Cover picture"
        description="Shown on the program's card wherever members see it."
      >
        <div className="flex flex-col items-start gap-2">
          {pickedPreview ? (
            <img
              src={pickedPreview}
              alt="New cover, uploading"
              className="aspect-video w-48 rounded-xl object-cover opacity-60"
            />
          ) : cover ? (
            <VariantImg
              src={cover}
              size="400x400"
              surface="coverArt"
              alt="Current cover"
              className="aspect-video w-48 rounded-xl object-cover"
              data-testid="settings-cover-image"
            />
          ) : (
            <span
              className="flex aspect-video w-48 items-center justify-center rounded-xl bg-muted text-xs text-muted-foreground"
              data-testid="settings-cover-empty"
            >
              No cover picture yet
            </span>
          )}
          {canManage && (
            <>
              <input
                ref={coverInput}
                type="file"
                accept="image/*"
                className="sr-only"
                tabIndex={-1}
                aria-hidden="true"
                onChange={(e) => void changeCover(e)}
                data-testid="settings-cover-input"
              />
              <Button
                type="button"
                variant="outline"
                className="min-h-11"
                disabled={coverSaving}
                onClick={() => coverInput.current?.click()}
              >
                {cover ? "Change cover picture" : "Add a cover picture"}
              </Button>
            </>
          )}
          {coverSaving && (
            <p role="status" className="text-sm text-muted-foreground">
              Uploading your picture…
            </p>
          )}
          {coverError && (
            <p role="alert" className="text-sm text-destructive" data-testid="settings-cover-error">
              {coverError}
            </p>
          )}
        </div>
      </SettingsCard>

      <SettingsCard
        id="public"
        title="Who can find it"
        description={
          isPT
            ? "A 1-to-1 program is private to the coach, the athlete and the gym's admins."
            : "Your public page is where new people browse and join your programs."
        }
      >
        {isPT ? (
          <p className="text-sm" data-testid="program-public-pt">
            Invitation only. It never shows on your public page: the coach invites the athlete.
          </p>
        ) : canManage ? (
          <>
            <SwitchRow
              id="program-landing-page"
              label="Show on your public page"
              hint={
                archived
                  ? "Archived programs can't be on your public page."
                  : toggleValue("landingPage")
                    ? "Anyone can find it on your public page and join."
                    : "Hidden. Only people you invite or who already follow it see it."
              }
              checked={!archived && toggleValue("landingPage")}
              disabled={archived || toggleSaving !== null}
              onChange={(next) => void setToggle("landingPage", next)}
            />
            {toggleErrorFor(["landingPage"])}
          </>
        ) : (
          <p className="text-sm">
            {toggleValue("landingPage") && !archived
              ? "On your public page. Anyone can find it and join."
              : "Not on your public page."}
          </p>
        )}
      </SettingsCard>

      {daily && (
        <SettingsCard
          id="members-see"
          title="What members see"
          description="How far back and how far ahead members can look in the calendar."
        >
          {canManage ? (
            <div className="flex flex-col gap-4">
              {!isPT && (
                <SwitchRow
                  id="program-history"
                  label="New members can see past workouts"
                  hint={
                    toggleValue("isHistoryAvailable")
                      ? "Someone who joins today can look back at every workout since the program began."
                      : "Someone who joins today sees workouts from the day they joined."
                  }
                  checked={toggleValue("isHistoryAvailable")}
                  disabled={archived || toggleSaving !== null}
                  onChange={(next) => void setToggle("isHistoryAvailable", next)}
                />
              )}
              <SwitchRow
                id="program-release-all"
                label="Show all planned workouts at once"
                hint={
                  toggleValue("releaseAll")
                    ? "Members can see every workout you've planned, however far ahead."
                    : "Members see this week's workouts. Next week's appear on Sunday."
                }
                checked={toggleValue("releaseAll")}
                disabled={archived || toggleSaving !== null}
                onChange={(next) => void setToggle("releaseAll", next)}
              />
              {toggleErrorFor(["isHistoryAvailable", "releaseAll"])}
            </div>
          ) : (
            <dl className="grid gap-4 sm:grid-cols-2">
              {!isPT && (
                <Fact label="New members can see past workouts">
                  {onOff(toggleValue("isHistoryAvailable"))}
                </Fact>
              )}
              <Fact label="Show all planned workouts at once">
                {onOff(toggleValue("releaseAll"))}
              </Fact>
            </dl>
          )}
          <p className="text-xs text-muted-foreground" data-testid="program-members-see-web-only">
            Today only the web dashboard follows these. The Lemonade phone app doesn't yet.
          </p>
        </SettingsCard>
      )}

      <SettingsCard
        id="facts"
        title="How it runs and price"
        description="Set when the program was created."
      >
        <dl className="grid gap-4 sm:grid-cols-2">
          <Fact label="How it runs">{typeLabel}</Fact>
          <Fact label="Price">{priceLabel}</Fact>
        </dl>
        <p className="text-sm text-muted-foreground">
          These can't be changed after a program is created. To run it differently or at another
          price, create a new program.
        </p>
      </SettingsCard>

      {canManage && (
        <SettingsCard
          id="danger"
          title={archived ? "Archived" : "Archive or end"}
          description={
            archived
              ? "This program is archived. Nobody new can join, and it can't be reopened yet."
              : "Stop new people joining, or end the program for everyone."
          }
        >
          <div className="flex flex-col gap-3">
            {!archived && (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="max-w-prose text-sm">
                  <b>Archive:</b> nobody new can join. Current members keep access.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-11 text-destructive"
                  onClick={() => {
                    setArchiveError(null);
                    setArchiveOpen(true);
                  }}
                  data-testid="program-archive"
                >
                  Archive…
                </Button>
              </div>
            )}
            {mayEnd && (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="max-w-prose text-sm">
                  <b>End for everyone:</b> also ends it for current members. We show you how many
                  first.
                </p>
                <Button
                  type="button"
                  variant="destructive"
                  className="min-h-11"
                  disabled={discontinue.isRowBusy(programmingUID)}
                  onClick={() =>
                    void discontinue.requestDiscontinue({
                      id: programmingUID,
                      name: program.name,
                      pricingType: program.pricing?.type,
                    })
                  }
                  data-testid="program-end-for-everyone"
                >
                  End for everyone…
                </Button>
              </div>
            )}
          </div>
        </SettingsCard>
      )}

      <ArchiveProgramDialog
        open={archiveOpen}
        name={program.name}
        busy={archiving}
        error={archiveError}
        onConfirm={() => void confirmArchive()}
        onClose={() => setArchiveOpen(false)}
      />
      <DiscontinueProductDialog
        open={discontinue.target !== null}
        name={discontinue.target?.name ?? ""}
        activeSubscriptionCount={discontinue.target?.activeSubscriptionCount ?? null}
        stripeWoundDown={discontinue.target?.stripeWoundDown !== false}
        noun="subscription"
        busy={discontinue.busy}
        onConfirm={discontinue.confirmDiscontinue}
        onClose={discontinue.cancel}
      />
    </div>
  );
}

export default ProgramSettingsTab;
